/**
 * SelfRegisterPage
 * ----------------
 * Public self-service registration for Contractor + Supplier accounts.
 * Routed from /register?type=contractor and /register?type=supplier.
 *
 * Single full-profile form (no email-magic-link two-step) that collects:
 *   • Business identity (company name, contact, phone, address, trade)
 *   • Service area / business details
 *   • Password (we issue access after admin approval)
 *   • Onboarding documents (COI, W-9, license, trade certification — multi-upload)
 *
 * Submit flow:
 *   1. POST /api/public/contractors/register-self (or /suppliers/register-self)
 *      → server creates a `pending_approval` user account and emails admins.
 *   2. With the returned user_id, the client uploads each attached file via
 *      POST /api/public/onboarding/upload-document (multipart).
 *   3. UI flips to a "Pending approval" success card with instructions.
 *
 * Hospital registrations still flow through the simpler magic-link form on
 * PublicRegisterPage.jsx (this page only handles contractor/supplier).
 */
import { useState, useMemo } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, ShieldCheck, Upload, FileCheck2, Building2, ArrowLeft,
  CheckCircle, X, Mail, MapPin, Phone, Award,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/App";

const API = process.env.REACT_APP_BACKEND_URL;

const STERILE_MATE_LOGO = "/images/sterilemate-logo.jpg";

const CONTRACTOR_DOCS = [
  { key: "coi", label: "Certificate of Insurance (COI)", required: true,
    help: "General Liability + Workers' Comp · we'll be listed as an additional insured." },
  { key: "w9", label: "W-9 Tax Form", required: true,
    help: "Used for 1099 payment reporting." },
  { key: "trade_license", label: "Trade License", required: true,
    help: "Current state / municipal license for your trade." },
  { key: "trade_certification", label: "Trade Certifications (optional)", required: false,
    help: "OEM training, joint commission, NFPA, etc. Attach any that apply." },
  { key: "signed_service_agreement", label: "Signed Service Agreement (optional)", required: false,
    help: "Upload now or sign after approval." },
];

const SUPPLIER_DOCS = [
  { key: "w9", label: "W-9 Tax Form", required: true,
    help: "Required for accounts-payable setup." },
  { key: "business_license", label: "Business License", required: true,
    help: "Current state business registration." },
  { key: "distributor_authorization", label: "Distributor Authorization (optional)", required: false,
    help: "Manufacturer letter / distributor agreement if applicable." },
  { key: "product_catalog", label: "Product Catalog / Line Card (optional)", required: false,
    help: "PDF or spreadsheet helps us route RFQs faster." },
];

const TRADES = [
  "Plumbing", "Electrical", "HVAC", "Carpentry", "Painting", "Flooring",
  "Roofing", "General Construction", "Medical Equipment Repair", "Other",
];

const SUPPLIER_TYPES = [
  "Authorized Distributor", "Wholesaler", "Manufacturer Rep",
  "Grey Market / Hard-to-find", "OEM Direct", "Other",
];

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY",
];

function DocSlot({ doc, file, uploading, onPick, onClear }) {
  return (
    <div
      className={`p-3 rounded-lg border ${file ? "bg-emerald-50 border-emerald-300" : "bg-slate-50 border-slate-200"} transition-colors`}
      data-testid={`doc-slot-${doc.key}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-slate-800 truncate">{doc.label}</span>
            {doc.required && <span className="text-[10px] uppercase tracking-wider font-bold text-rose-600">Required</span>}
          </div>
          <p className="text-xs text-slate-500 mt-0.5">{doc.help}</p>
          {file && (
            <div className="mt-1 inline-flex items-center gap-1.5 text-xs text-emerald-700">
              <FileCheck2 className="w-3.5 h-3.5" />
              <span className="font-mono truncate max-w-[200px]">{file.name}</span>
              <button type="button" onClick={onClear} className="text-rose-600 hover:underline ml-2">remove</button>
            </div>
          )}
        </div>
        <label className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md cursor-pointer text-xs font-bold shrink-0 ${file ? "bg-white border border-emerald-300 text-emerald-700" : "bg-emerald-600 text-white hover:bg-emerald-700"}`}>
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
          {file ? "Replace" : "Upload"}
          <input
            type="file"
            className="hidden"
            accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx"
            onChange={(e) => onPick(e.target.files?.[0])}
            data-testid={`doc-input-${doc.key}`}
          />
        </label>
      </div>
    </div>
  );
}

export default function SelfRegisterPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const type = params.get("type") === "supplier" ? "supplier" : "contractor";
  const docs = useMemo(() => (type === "supplier" ? SUPPLIER_DOCS : CONTRACTOR_DOCS), [type]);

  const [form, setForm] = useState({
    business_name: "",
    contact_name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    trade: "",
    business_type: "",
    license_number: "",
    years_experience: "",
    service_area: "",
    website: "",
    password: "",
    confirm_password: "",
    notes: "",
  });
  const [files, setFiles] = useState({});            // {coi: File, w9: File, ...}
  const [submitting, setSubmitting] = useState(false);
  const [uploadingKey, setUploadingKey] = useState(null);
  const [pendingState, setPendingState] = useState(null); // {user_id, email}

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pickFile = (key, file) => {
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      toast.error("File too large — max 20 MB");
      return;
    }
    setFiles((f) => ({ ...f, [key]: file }));
  };

  const clearFile = (key) => setFiles((f) => { const n = { ...f }; delete n[key]; return n; });

  const validate = () => {
    if (!form.business_name.trim()) return "Business name is required";
    if (!form.contact_name.trim()) return "Contact name is required";
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return "Valid email is required";
    if (!form.phone.trim()) return "Phone is required";
    if (!form.city.trim() || !form.state.trim()) return "City and state are required";
    if (type === "contractor" && !form.trade) return "Trade is required";
    if (type === "supplier" && !form.business_type) return "Business type is required";
    if (form.password.length < 8) return "Password must be at least 8 characters";
    if (form.password !== form.confirm_password) return "Passwords do not match";
    const missingRequired = docs.filter((d) => d.required && !files[d.key]);
    if (missingRequired.length > 0) {
      return `Please attach: ${missingRequired.map((d) => d.label).join(", ")}`;
    }
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      // 1. Create the pending account
      const path = type === "supplier" ? "suppliers/register-self" : "contractors/register-self";
      const payload = type === "supplier"
        ? {
            company_name: form.business_name.trim(),
            contact_name: form.contact_name.trim(),
            email: form.email.trim(),
            password: form.password,
            phone: form.phone.trim(),
            city: form.city.trim(),
            state: form.state.trim().toUpperCase(),
            business_type: form.business_type,
            product_categories: form.trade ? form.trade : null,
            website: form.website.trim() || null,
          }
        : {
            business_name: form.business_name.trim(),
            contact_name: form.contact_name.trim(),
            email: form.email.trim(),
            password: form.password,
            phone: form.phone.trim(),
            city: form.city.trim(),
            state: form.state.trim().toUpperCase(),
            trade: form.trade,
            license_number: form.license_number.trim() || null,
            years_experience: form.years_experience ? parseInt(form.years_experience, 10) : null,
            service_area: form.service_area.trim() || `${form.city}, ${form.state}`,
            referral_code: null,
          };
      const res = await fetch(`${API}/api/${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data?.detail || data?.message || "Registration failed");
      }

      // 2. Upload each attached document — track per-slot success/failure so
      //    we never show "Application Submitted ✓ N docs uploaded" if some
      //    actually 500'd silently.
      const userId = data.user_id;
      const uploadResults = {}; // {coi: true, w9: false, ...}
      for (const doc of docs) {
        const f = files[doc.key];
        if (!f) continue;
        setUploadingKey(doc.key);
        const fd = new FormData();
        fd.append("user_id", userId);
        fd.append("email", form.email.trim());
        fd.append("document_type", doc.key);
        fd.append("file", f);
        try {
          const r = await fetch(`${API}/api/onboarding/upload-document`, { method: "POST", body: fd });
          uploadResults[doc.key] = r.ok;
        } catch {
          uploadResults[doc.key] = false;
        }
      }
      setUploadingKey(null);

      const failedDocs = docs.filter((d) => files[d.key] && uploadResults[d.key] === false);
      const successCount = Object.values(uploadResults).filter(Boolean).length;
      setPendingState({
        user_id: userId,
        email: form.email,
        uploaded_count: successCount,
        failed: failedDocs.map((d) => d.label),
        token: data.token,
        user: data.user,
      });
      if (failedDocs.length === 0) {
        toast.success("Welcome to Sterile Mate — your account is live!");
      } else {
        toast.warning(
          `Account created, but ${failedDocs.length} document upload${failedDocs.length === 1 ? "" : "s"} failed. You can attach them later from your dashboard.`,
        );
      }
    } catch (e) {
      toast.error(e.message || "Could not submit. Please try again or call 281.456.3458.");
    } finally {
      setSubmitting(false);
    }
  };

  const goToPortal = () => {
    if (pendingState?.token && pendingState?.user) {
      login(pendingState.token, pendingState.user);
    }
    navigate(type === "supplier" ? "/supplier/dashboard" : "/contractor/dashboard");
  };

  if (pendingState) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-2xl border border-emerald-200 max-w-xl w-full p-8 text-center" data-testid="registration-success-card">
          <img src={STERILE_MATE_LOGO} alt="Sterile Mate" className="h-14 mx-auto mb-4" />
          <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">You&apos;re in!</h1>
          <p className="text-slate-600 mb-4">
            Welcome to the Sterile Mate {type === "supplier" ? "Supplier" : "Contractor"} Network — your account is <strong className="text-emerald-700">active</strong>.
          </p>
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-sm text-emerald-900 text-left space-y-2 mb-5">
            <div className="flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />
              <span>Signed in as <strong>{pendingState.email}</strong>. Password is set — you can log in from any device.</span>
            </div>
            <div className="flex items-start gap-2">
              <FileCheck2 className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />
              <span>{pendingState.uploaded_count} document{pendingState.uploaded_count === 1 ? "" : "s"} uploaded — our compliance team will confirm your credentials in the background.</span>
            </div>
            {pendingState.failed && pendingState.failed.length > 0 && (
              <div className="flex items-start gap-2 text-amber-800 bg-amber-50 -mx-2 px-2 py-1.5 rounded border border-amber-200">
                <X className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
                <span>
                  {pendingState.failed.length} upload{pendingState.failed.length === 1 ? "" : "s"} failed:{" "}
                  <strong>{pendingState.failed.join(", ")}</strong>. You can re-attach them from your dashboard.
                </span>
              </div>
            )}
            <div className="flex items-start gap-2">
              <Mail className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />
              <span>Welcome letter with next steps is on its way to your inbox.</span>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <Button
              onClick={goToPortal}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold h-11 px-6"
              data-testid="sr-goto-portal"
            >
              Go to my {type === "supplier" ? "Supplier" : "Contractor"} Dashboard
            </Button>
            <Link
              to="/"
              className="inline-flex items-center justify-center gap-1 h-11 px-4 text-sm font-bold text-slate-600 hover:text-slate-900"
            >
              <ArrowLeft className="w-4 h-4" /> Back to home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const title = type === "supplier" ? "Join the Sterile Mate Supplier Network" : "Join the Sterile Mate Contractor Network";
  const subtitle = type === "supplier"
    ? "Get instant access to hospital RFQs. Your account goes live the moment you submit — no waiting."
    : "Get instant access to hospital work orders. Your account goes live the moment you submit — no waiting.";

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 mb-4">
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200">
          <div className="p-6 md:p-8 border-b border-slate-200">
            <div className="flex items-center gap-3 mb-3">
              <img src={STERILE_MATE_LOGO} alt="Sterile Mate" className="h-10" />
              <span className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                {type === "supplier" ? "Supplier" : "Contractor"} sign-up
              </span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900">{title}</h1>
            <p className="text-slate-600 mt-1.5">{subtitle}</p>
          </div>

          <form onSubmit={submit} className="p-6 md:p-8 space-y-6" data-testid="self-register-form">
            {/* Business identity */}
            <section>
              <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-3 inline-flex items-center gap-2">
                <Building2 className="w-4 h-4" /> Business Identity
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <Label>Company / Business Name *</Label>
                  <Input value={form.business_name} onChange={(e) => setField("business_name", e.target.value)} placeholder="ACME Plumbing & Heating" data-testid="sr-business-name" />
                </div>
                <div>
                  <Label>Contact Name *</Label>
                  <Input value={form.contact_name} onChange={(e) => setField("contact_name", e.target.value)} placeholder="Jane Smith" data-testid="sr-contact-name" />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} placeholder="you@business.com" data-testid="sr-email" />
                </div>
                <div>
                  <Label>Phone *</Label>
                  <Input value={form.phone} onChange={(e) => setField("phone", e.target.value)} placeholder="(281) 555-0123" data-testid="sr-phone" />
                </div>
                <div>
                  <Label>Website</Label>
                  <Input value={form.website} onChange={(e) => setField("website", e.target.value)} placeholder="https://example.com" />
                </div>
              </div>
            </section>

            {/* Address */}
            <section>
              <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-3 inline-flex items-center gap-2">
                <MapPin className="w-4 h-4" /> Service Address
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-3">
                <div className="sm:col-span-12">
                  <Label>Street</Label>
                  <Input value={form.address} onChange={(e) => setField("address", e.target.value)} placeholder="123 Main St" />
                </div>
                <div className="sm:col-span-5">
                  <Label>City *</Label>
                  <Input value={form.city} onChange={(e) => setField("city", e.target.value)} data-testid="sr-city" />
                </div>
                <div className="sm:col-span-3">
                  <Label>State *</Label>
                  <select
                    value={form.state}
                    onChange={(e) => setField("state", e.target.value)}
                    className="w-full h-10 rounded-md border border-slate-300 px-2 bg-white"
                    data-testid="sr-state"
                  >
                    <option value="">—</option>
                    {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-4">
                  <Label>ZIP</Label>
                  <Input value={form.zip} onChange={(e) => setField("zip", e.target.value)} />
                </div>
                {type === "contractor" && (
                  <div className="sm:col-span-12">
                    <Label>Service Area</Label>
                    <Input
                      value={form.service_area}
                      onChange={(e) => setField("service_area", e.target.value)}
                      placeholder="e.g. Greater Houston metro · 75 mile radius"
                    />
                  </div>
                )}
              </div>
            </section>

            {/* Trade / business type */}
            <section>
              <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-3 inline-flex items-center gap-2">
                <Award className="w-4 h-4" /> {type === "supplier" ? "Supplier Details" : "Trade Details"}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {type === "contractor" ? (
                  <>
                    <div>
                      <Label>Primary Trade *</Label>
                      <select
                        value={form.trade}
                        onChange={(e) => setField("trade", e.target.value)}
                        className="w-full h-10 rounded-md border border-slate-300 px-2 bg-white"
                        data-testid="sr-trade"
                      >
                        <option value="">— Select —</option>
                        {TRADES.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div>
                      <Label>License #</Label>
                      <Input value={form.license_number} onChange={(e) => setField("license_number", e.target.value)} />
                    </div>
                    <div>
                      <Label>Years in business</Label>
                      <Input type="number" min={0} value={form.years_experience} onChange={(e) => setField("years_experience", e.target.value)} />
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <Label>Supplier Type *</Label>
                      <select
                        value={form.business_type}
                        onChange={(e) => setField("business_type", e.target.value)}
                        className="w-full h-10 rounded-md border border-slate-300 px-2 bg-white"
                        data-testid="sr-business-type"
                      >
                        <option value="">— Select —</option>
                        {SUPPLIER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div>
                      <Label>Primary Category</Label>
                      <Input value={form.trade} onChange={(e) => setField("trade", e.target.value)} placeholder="e.g. Sterilization, Surgical, ENT" />
                    </div>
                  </>
                )}
              </div>
            </section>

            {/* Account */}
            <section>
              <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-3 inline-flex items-center gap-2">
                <ShieldCheck className="w-4 h-4" /> Set Your Password
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Password *</Label>
                  <Input type="password" value={form.password} onChange={(e) => setField("password", e.target.value)} placeholder="At least 8 characters" data-testid="sr-password" />
                </div>
                <div>
                  <Label>Confirm Password *</Label>
                  <Input type="password" value={form.confirm_password} onChange={(e) => setField("confirm_password", e.target.value)} data-testid="sr-confirm-password" />
                </div>
              </div>
              <p className="text-[11px] text-slate-500 mt-1.5">You&apos;ll be signed in instantly after submitting — no waiting for approval.</p>
            </section>

            {/* Documents */}
            <section>
              <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-3 inline-flex items-center gap-2">
                <Upload className="w-4 h-4" /> Compliance Documents
              </h2>
              <p className="text-xs text-slate-500 mb-3">
                Attach the required documents below. Files up to 20 MB · PDF / PNG / JPG / DOC / XLS accepted.
              </p>
              <div className="space-y-2">
                {docs.map((d) => (
                  <DocSlot
                    key={d.key}
                    doc={d}
                    file={files[d.key]}
                    uploading={uploadingKey === d.key}
                    onPick={(f) => pickFile(d.key, f)}
                    onClear={() => clearFile(d.key)}
                  />
                ))}
              </div>
            </section>

            {/* Notes */}
            <section>
              <Label className="text-sm font-bold text-slate-700">Anything we should know? (optional)</Label>
              <Textarea
                rows={2}
                value={form.notes}
                onChange={(e) => setField("notes", e.target.value)}
                placeholder="Coverage availability, special certifications, referral source…"
                className="mt-1.5"
              />
            </section>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-3 border-t border-slate-200">
              <p className="text-xs text-slate-500">
                By submitting, you agree to a background compliance review. You&apos;ll be signed in immediately.
              </p>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 h-11 font-bold"
                data-testid="sr-submit"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating account…</>
                ) : (
                  <>Create My Account</>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
